<template>
  <Dialog v-model:visible="dialogVisible" modal maximizable :header="title" :style="{ width: deletionReport ? '65rem' : width, maxWidth: '95vw', height: '80vh' }" :contentStyle="{ flex: '1 1 auto', minHeight: '0', overflowY: 'auto' }" :closable="!deleting" :closeOnEscape="!deleting">
    <p v-if="deletionReport" role="status" class="text-green-600">{{ deletionReport.message }}</p>
    <DataTable v-model:first="first" :key="deletionReport ? 'deletion-report' : 'file-details'" :value="filteredRows" :loading="loading" stripedRows rowHover paginator :rows="10" :rowsPerPageOptions="[10, 20, 50]" responsiveLayout="scroll">
      <template #header>
        <div class="flex flex-wrap align-items-center justify-content-between gap-2">
          <span>{{ filename }}</span>
          <div v-if="pointFields.length && !deletionReport" class="flex flex-wrap align-items-center gap-2">
            <InputText v-model="pointSearch" placeholder="Cerca COD_PDR / COD_POD" aria-label="Cerca COD_PDR o COD_POD" @update:modelValue="first = 0" />
            <Button label="Reset ricerca" icon="pi pi-times" class="p-button-outlined" style="border: 1px solid currentColor;" :disabled="!pointSearch" @click="pointSearch = ''; first = 0" />
          </div>
          <div class="flex align-items-center gap-2">
            <Checkbox v-if="deleteRows && !deletionReport" inputId="select-all-request-rows" v-model="allSelected" :binary="true" :disabled="loading || deleting || !rows.length" class="mr-2"/>
            <label v-if="deleteRows && !deletionReport" for="select-all-request-rows">Seleziona tutte le righe del file, anche se filtrate ({{ selectedIds.length }}/{{ rows.length }})</label>
            <Button v-if="download && !deletionReport?.file_deleted" label="Scarica file" icon="pi pi-download" style="font-size: 14px;" :loading="downloading" :disabled="deleting" @click="downloadFile" />
          </div>
        </div>
      </template>
      <template #empty>{{ error || 'Nessun dato disponibile per questo file.' }}</template>
      <Column v-if="deleteRows && !deletionReport" key="row-selection" headerStyle="width: 3rem">
        <template #body="{ data }">
          <Checkbox :modelValue="selectedIds.includes(data.__supply_id)" :binary="true" :disabled="deleting" @update:modelValue="toggleSelection(data.__supply_id, $event)" />
        </template>
      </Column>
      <Column v-for="column in columns" :key="column.field" :field="column.field" :header="column.header" />
    </DataTable>
    <template v-if="deleteRows" #footer>
      <Button v-if="!deletionReport" :label="allSelected ? 'Elimina tutti' : 'Elimina selezionati'" icon="pi pi-trash" class="p-button-danger" style="font-size: 14px;" :loading="deleting" :disabled="loading || !selectedIds.length" @click="deleteSelection" />
      <template v-else>
        <span class="mr-3">Chiusura automatica tra {{ reportCloseSeconds }} {{ reportCloseSeconds === 1 ? 'secondo' : 'secondi' }}</span>
        <Button label="Chiudi" style="font-size: 14px;" @click="dialogVisible = false" />
      </template>
    </template>
  </Dialog>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';

export default {
  props: {
    visible: { type: Boolean, default: false },
    filename: { type: String, required: true },
    title: { type: String, default: 'Dettaglio file' },
    width: { type: String, default: '90vw' },
    loadData: { type: Function, required: true },
    download: { type: Function, default: null },
    deleteRows: { type: Function, default: null },
  },
  emits: ['update:visible', 'deleted'],
  setup(props, { emit }) {
    const toast = useToast();
    const rows = ref([]);
    const pointSearch = ref('');
    const first = ref(0);
    const pointFields = computed(() => columns.value.filter(column =>
      [column.header, column.field].some(value => ['COD_PDR', 'COD_POD'].includes(String(value ?? '').trim().toUpperCase()))
    ).map(column => column.field));
    const filteredRows = computed(() => {
      const search = pointSearch.value.trim().toLowerCase();
      if (!search || deletionReport.value || !pointFields.value.length) return rows.value;
      return rows.value.filter(row => pointFields.value.some(field => String(row[field] ?? '').toLowerCase().includes(search)));
    });
    const suppliedColumns = ref([]);
    const loading = ref(false);
    const downloading = ref(false);
    const error = ref('');
    const selectedIds = ref([]);
    const fileVersion = ref('');
    const deleting = ref(false);
    const deletionReport = ref(null);
    const reportCloseSeconds = ref(10);
    watch(deletionReport, (report, previous, onCleanup) => {
      if (!report) return;
      reportCloseSeconds.value = 10;
      const deadline = Date.now() + 10000;
      const timer = setInterval(() => {
        reportCloseSeconds.value = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
        if (reportCloseSeconds.value === 0) {
          clearInterval(timer);
          dialogVisible.value = false;
        }
      }, 250);
      onCleanup(() => clearInterval(timer));
    });
    const allSelected = computed({
      get: () => rows.value.length > 0 && selectedIds.value.length === rows.value.length,
      set: value => { selectedIds.value = value ? rows.value.map(row => row.__supply_id) : []; },
    });
    const toggleSelection = (id, checked) => {
      selectedIds.value = checked ? [...new Set([...selectedIds.value, id])] : selectedIds.value.filter(value => value !== id);
    };
    const dialogVisible = computed({
      get: () => props.visible,
      set: value => emit('update:visible', value),
    });
    const columns = computed(() => suppliedColumns.value.length ? suppliedColumns.value :
      [...new Set(rows.value.flatMap(row => Object.keys(row)))].map(field => ({ field, header: field })));

    watch(() => [props.visible, props.filename, props.loadData], async ([visible, filename, loadData], previous, onCleanup) => {
      let active = true;
      onCleanup(() => { active = false; });
      rows.value = [];
      pointSearch.value = '';
      first.value = 0;
      suppliedColumns.value = [];
      error.value = '';
      selectedIds.value = [];
      fileVersion.value = '';
      deletionReport.value = null;
      loading.value = false;
      if (!visible) return;

      loading.value = true;
      try {
        const result = await loadData(filename);
        if (active) {
          if (props.deleteRows && (!result.version || result.selection_ids?.length !== result.items.length)) {
            throw new Error('Identificativi delle righe non disponibili');
          }
          rows.value = props.deleteRows ? result.items.map((row, index) => ({ ...row, __supply_id: result.selection_ids[index] })) : result.items;
          suppliedColumns.value = result.columns || [];
          fileVersion.value = result.version || '';
        }
      } catch (failure) {
        if (active) error.value = failure.response?.data?.message || 'Impossibile recuperare i dati del file';
      } finally {
        if (active) loading.value = false;
      }
    }, { immediate: true });

    const downloadFile = async () => {
      if (!props.download || downloading.value) return;
      downloading.value = true;
      try {
        await props.download(props.filename);
      } catch (failure) {
        toast.add({ severity: 'error', summary: 'Download non riuscito', detail: 'Impossibile scaricare il file richiesto', life: 5000 });
      } finally {
        downloading.value = false;
      }
    };

    const deleteSelection = async () => {
      if (!props.deleteRows || deleting.value || !selectedIds.value.length) return;
      deleting.value = true;
      try {
        const result = await props.deleteRows({
          filename: props.filename,
          type_delete: allSelected.value ? 'all' : 'ids',
          ids: selectedIds.value,
          version: fileVersion.value,
        });
        deletionReport.value = result;
        first.value = 0;
        rows.value = result.report.items;
        suppliedColumns.value = result.report.columns;
        selectedIds.value = [];
        emit('deleted', result);
      } catch (failure) {
        toast.add({ severity: 'error', summary: 'Eliminazione non riuscita', detail: failure.response?.data?.message || 'Impossibile eliminare le righe selezionate', life: 10000 });
      } finally {
        deleting.value = false;
      }
    };

    return { dialogVisible, rows, columns, pointSearch, first, pointFields, filteredRows, loading, downloading, error, downloadFile, selectedIds, allSelected, toggleSelection, deleting, deletionReport, reportCloseSeconds, deleteSelection };
  },
};
</script>
